import { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import {
  TECH_SOURCE_MODES,
  TECH_SOURCE_MODE_LABELS,
  techWertText,
  type TechField,
  type TechSourceMode,
  type TechSourceValue,
  type TechUserValue,
} from '@symphenia/shared-types'
import { inlineInput } from '@/lib/form-styles'
import { fetchProjects } from '../api/projects.api'
import { fetchPartner, type PartnerAuswahl } from '../api/projects-head.api'
import type { LookupUser } from '../types/project.types'
import { KundenSuche, RollenBenutzer } from './auswahl'

/**
 * AP-35 Nachtrag 4 (19.9.2026) — **Eingaben des Technik-Blatts, die mehr sind als ein Feld.**
 *
 * - `VorschlagText`: frei tippen **oder** aus der Liste wählen. Der Pfeil rechts
 *   ist ein unsichtbares `<select>` über dem Knopf — so bleibt das Wählen ein
 *   natives Aufklappen, ohne eigene Liste, die über den Kartenrand ragt.
 * - `MitarbeiterWahl`: Mitarbeiter, vorgefiltert nach Projektrolle (`options[0]`).
 * - `QuelleWahl`: Statik und Planung — „keine / Bereitgestellt von / Abgeleitet
 *   von Projekt / Extern von" plus Partner und „Verweis auf" (Ansprechpartner).
 *
 * Geerbte Werte stehen wie überall im Technik-Blatt als Platzhalter, nicht als Wert.
 */

const LISTE = 'absolute left-0 top-full z-50 mt-1 max-h-64 w-full min-w-[18rem] overflow-auto rounded-md border border-border shadow-lg'
const listenStil = { background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }

export function VorschlagText({
  feld,
  wert,
  platzhalter,
  deaktiviert,
  onChange,
  onBlur,
  onWahl,
}: {
  feld: TechField
  wert: unknown
  platzhalter?: string
  deaktiviert: boolean
  onChange: (v: string) => void
  /** AP-36 Fall 1: Gespeichert wird beim Verlassen, nicht beim Tippen. */
  onBlur?: () => void
  /** Aus der Vorschlagsliste gewählt — eine Auswahl speichert sofort (3.2). Ohne Angabe wie `onChange`. */
  onWahl?: (v: string) => void
}) {
  const text = wert === null || wert === undefined ? '' : String(wert)
  const optionen = feld.options ?? []
  const gemeinsam = {
    className: `${inlineInput} ${optionen.length ? 'pr-8' : ''}`,
    disabled: deaktiviert,
    value: text,
    placeholder: platzhalter,
    onBlur,
  }
  return (
    <div className="relative">
      {feld.multiline ? (
        <textarea {...gemeinsam} rows={2} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input {...gemeinsam} type="text" onChange={(e) => onChange(e.target.value)} />
      )}
      {optionen.length > 0 && !deaktiviert && (
        <span className="absolute right-0 top-0 flex h-[34px] w-7 items-center justify-center border-l border-input text-muted-foreground">
          <ChevronDown size={14} />
          <select
            className="absolute inset-0 cursor-pointer opacity-0"
            aria-label={`${feld.label} — Vorschlag wählen`}
            value=""
            onChange={(e) => e.target.value && (onWahl ?? onChange)(e.target.value)}
          >
            <option value="">—</option>
            {optionen.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </span>
      )}
    </div>
  )
}

export function MitarbeiterWahl({
  feld,
  wert,
  geerbt,
  benutzer,
  deaktiviert,
  onChange,
}: {
  feld: TechField
  wert: unknown
  geerbt?: string
  benutzer: LookupUser[]
  deaktiviert: boolean
  onChange: (v: TechUserValue | null) => void
}) {
  const aktuell = (wert as TechUserValue | null)?.id ?? ''
  // Ist die Person nicht mehr in der Liste (ausgetreten), bleibt ihr Name stehen.
  const liste = aktuell && !benutzer.some((b) => b.id === aktuell) ? [...benutzer, { id: aktuell, name: (wert as TechUserValue).name, email: '' }] : benutzer
  return (
    <fieldset disabled={deaktiviert} className="contents">
      <RollenBenutzer
        benutzer={liste}
        rolle={feld.options?.[0] ?? null}
        wert={aktuell}
        leer={geerbt ?? '— niemand —'}
        className={`${inlineInput} ${!aktuell && geerbt ? 'text-muted-foreground' : ''}`}
        onChange={(id) => {
          const b = liste.find((x) => x.id === id)
          onChange(b ? { id: b.id, name: b.name } : null)
        }}
      />
    </fieldset>
  )
}

export function QuelleWahl({
  wert,
  geerbt,
  begriffProjekt,
  deaktiviert,
  onChange,
}: {
  wert: unknown
  geerbt?: string
  begriffProjekt: string
  deaktiviert: boolean
  onChange: (v: TechSourceValue | null) => void
}) {
  const q = (wert && typeof wert === 'object' ? wert : null) as TechSourceValue | null
  const [partner, setPartner] = useState<PartnerAuswahl | null>(null)
  const clientId = q?.clientId ?? null

  useEffect(() => {
    if (!clientId) {
      setPartner(null)
      return
    }
    let aktiv = true
    fetchPartner(clientId)
      .then((p) => aktiv && setPartner(p))
      .catch(() => aktiv && setPartner(null))
    return () => {
      aktiv = false
    }
  }, [clientId])

  const art = (m: TechSourceMode) => TECH_SOURCE_MODE_LABELS[m].replace('{projekt}', begriffProjekt)
  const mitPartner = q?.mode === 'PROVIDED' || q?.mode === 'EXTERNAL'

  return (
    <div className="space-y-1.5">
      <div className="grid gap-2 sm:grid-cols-[11rem_minmax(0,1fr)]">
        <select
          className={`${inlineInput} ${!q && geerbt ? 'text-muted-foreground' : ''}`}
          disabled={deaktiviert}
          value={q?.mode ?? ''}
          onChange={(e) => {
            const m = e.target.value as TechSourceMode | ''
            if (!m) return onChange(null)
            // Die Art wechselt, der Partner bleibt nur zwischen „Bereitgestellt" und „Extern" stehen.
            const behalten = (m === 'PROVIDED' || m === 'EXTERNAL') && mitPartner ? q : null
            onChange({ ...(behalten ?? {}), mode: m })
          }}
        >
          <option value="">{geerbt ?? '—'}</option>
          {TECH_SOURCE_MODES.map((m) => (
            <option key={m} value={m} className="text-foreground">
              {art(m)}
            </option>
          ))}
        </select>
        {mitPartner && (
          <KundenSuche
            allePartner
            deaktiviert={deaktiviert}
            platzhalter="Geschäftspartner suchen"
            wert={q?.clientId ? { id: q.clientId, name: q.clientName ?? '' } : null}
            onWahl={(k) => onChange({ mode: q!.mode, clientId: k?.id ?? null, clientName: k?.name ?? null, contactId: null, contactName: null })}
          />
        )}
        {q?.mode === 'DERIVED' && (
          <ProjektSuche
            deaktiviert={deaktiviert}
            platzhalter={`${begriffProjekt} suchen — Nummer oder Name`}
            wert={q.projectId ? { id: q.projectId, label: q.projectLabel ?? '' } : null}
            onWahl={(p) => onChange({ mode: 'DERIVED', projectId: p?.id ?? null, projectLabel: p?.label ?? null })}
          />
        )}
      </div>
      {mitPartner && q?.clientId && (
        <div className="grid items-center gap-2 sm:grid-cols-[11rem_minmax(0,1fr)]">
          <span className="text-xs text-muted-foreground sm:text-right">Verweis auf</span>
          <select
            className={inlineInput}
            disabled={deaktiviert}
            value={q.contactId ?? ''}
            onChange={(e) => {
              const k = partner?.contacts.find((c) => c.id === e.target.value)
              onChange({ ...q, contactId: k?.id ?? null, contactName: k ? [k.firstName, k.lastName].filter(Boolean).join(' ') : null })
            }}
          >
            <option value="">— niemand —</option>
            {q.contactId && !partner?.contacts.some((c) => c.id === q.contactId) && <option value={q.contactId}>{q.contactName}</option>}
            {partner?.contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {[c.firstName, c.lastName].filter(Boolean).join(' ')}
                {c.position ? ` · ${c.position}` : ''}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  )
}

function ProjektSuche({
  wert,
  onWahl,
  platzhalter,
  deaktiviert,
}: {
  wert: { id: string; label: string } | null
  onWahl: (p: { id: string; label: string } | null) => void
  platzhalter: string
  deaktiviert: boolean
}) {
  const [text, setText] = useState(wert?.label ?? '')
  const [offen, setOffen] = useState(false)
  const [treffer, setTreffer] = useState<{ id: string; label: string }[]>([])
  const zaehler = useRef(0)

  useEffect(() => setText(wert?.label ?? ''), [wert?.id, wert?.label])

  useEffect(() => {
    if (!offen || text.trim().length < 1 || text === wert?.label) {
      setTreffer([])
      return
    }
    const nr = ++zaehler.current
    const t = setTimeout(() => {
      fetchProjects({ search: text.trim(), limit: 20 })
        .then((r) => nr === zaehler.current && setTreffer(r.data.map((p) => ({ id: p.id, label: `${p.projectNumber} ${p.name}` }))))
        .catch(() => setTreffer([]))
    }, 250)
    return () => clearTimeout(t)
  }, [text, offen, wert?.label])

  return (
    <div className="relative">
      <input
        className={inlineInput}
        placeholder={platzhalter}
        disabled={deaktiviert}
        value={text}
        onFocus={() => setOffen(true)}
        onBlur={() => {
          setOffen(false)
          setText(wert?.label ?? '')
        }}
        onChange={(e) => setText(e.target.value)}
      />
      {offen && treffer.length > 0 && (
        <div className={LISTE} style={listenStil}>
          {treffer.map((p) => (
            <button
              key={p.id}
              type="button"
              tabIndex={-1}
              className="block w-full px-2 py-1.5 text-left text-sm hover:bg-muted"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onWahl(p)
                setText(p.label)
                setOffen(false)
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Geerbter Wert als Platzhaltertext — nur der Wert, grau (Auftraggeber 19.9.2026).
 * Woher er kommt, sagt das Schild „Projekt" neben der Beschriftung.
 */
export function geerbtText(geerbt: unknown, begriffProjekt: string): string | undefined {
  return techWertText(geerbt, begriffProjekt) || undefined
}
